// =============================================================================
// AuthContext.js — Application-wide authentication state
// =============================================================================
// Provides a React context that exposes: user, loading, error, login, logout, checkAuth.
// Wrap the entire app in <AuthProvider> (done in App.js) so any component can
// call useAuth() to get the current user or trigger sign-in/sign-out.
//
// Auth flow:
//   1. On mount, AuthProvider calls GET /auth/me to check the session cookie.
//   2. If the cookie is valid, `user` is populated with { displayName, email, ... }.
//   3. If not, `user` stays null and the app renders <LoginPage>.
//   4. login() redirects to GET /auth/login which starts the Azure AD OAuth flow.
//   5. Azure AD calls back to GET /auth/callback; the server sets a session cookie
//      and redirects back to the app.
//   6. logout() calls POST /auth/logout which clears the session and redirects
//      to the Azure AD logout endpoint so the browser session is also cleared.
//
// To add additional user fields (e.g. role, SiteId), update the /auth/me endpoint
// in server/routes/auth.js to include them, then use them via useAuth().user.
// =============================================================================
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi, setSessionExpiredHandler } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);    // null = not logged in; object = logged in user
  const [loading, setLoading] = useState(true); // true while we are checking the session
  const [error, setError] = useState(null);  // shown on the login page (e.g. "session expired")
  const logoutTriggered = useRef(false);     // guard against double-firing on concurrent 401s

  // Register the 401 handler from api.js — triggers auto-logout if the session
  // expires while the user is actively using the app (e.g. cookie expires mid-session).
  useEffect(() => {
    setSessionExpiredHandler(() => {
      if (logoutTriggered.current) return; // prevent double-fire
      logoutTriggered.current = true;
      setUser(null);
      setError('Your session has expired. Please sign in again.');
    });
    // Clean up handler on unmount
    return () => setSessionExpiredHandler(null);
  }, []);

  // Check whether the user is already authenticated via a session cookie.
  // This runs on every mount so the app never needs to hard-code auth state.
  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authApi.get('/auth/me');
      if (res.data.authenticated) {
        setUser(res.data.user); // user: { displayName, email, oid, ... }
      } else {
        setUser(null);
      }
      setError(null);
    } catch (err) {
      // 401 is expected when not logged in — not an error worth surfacing
      if (err.response && err.response.status === 401) {
        setUser(null);
      } else {
        console.error('Auth check failed:', err);
        setError('Unable to verify authentication.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Run checkAuth once when the provider first mounts
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Read ?authError=... from the URL — the server appends this after a failed OAuth callback.
  // We remove the param from the URL immediately so it doesn't persist on refresh.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('authError');
    if (authError) {
      setError(authError);
      // Remove only the authError param, preserve others (e.g. storeId, orderId)
      params.delete('authError');
      const remaining = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (remaining ? '?' + remaining : ''));
    }
  }, []);

  // login — redirect to the server's Azure AD login endpoint.
  // returnTo is used after the OAuth callback to send the user back to the page they were on.
  const login = (returnTo = '/') => {
    window.location.href = `/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
  };

  // logout — call the server to invalidate the session, then redirect to Azure AD logout.
  // The server returns a logoutUrl that clears the Azure AD session cookie as well.
  const logout = async () => {
    try {
      const res = await authApi.post('/auth/logout', {
        redirectTo: window.location.origin,
      });

      if (res.data.logoutUrl) {
        window.location.href = res.data.logoutUrl; // Full Azure AD sign-out
      } else {
        // Fallback if server doesn't return a logoutUrl
        setUser(null);
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Logout failed:', err);
      // Still clear local state even if the request fails
      setUser(null);
      window.location.href = '/';
    }
  };

  // Expose everything child components need via context value
  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

// useAuth — convenience hook. Throws if used outside <AuthProvider>.
// Usage: const { user, logout } = useAuth();
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export default AuthContext;
